import type { InspectionSettings, SheetMapping } from '../domain';
import type { ParsedWorkbookMeta } from '../features/inspection/parser';
import type {
  InspectionPayload,
  WorkerProgressHandler,
  WorkerRequest,
  WorkerResponse,
} from './protocol';

interface PendingRequest<T> {
  readonly resolve: (value: T) => void;
  readonly reject: (reason: Error) => void;
  readonly progress?: WorkerProgressHandler;
}

export class InspectionWorkerClient {
  private readonly worker: Worker;
  private readonly pending = new Map<string, PendingRequest<unknown>>();
  private requestSequence = 0;

  constructor() {
    this.worker = new Worker(new URL('./inspection.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      this.handleResponse(event.data);
    });
    this.worker.addEventListener('error', () => {
      this.rejectAll(new Error('检查线程异常终止，请重试。'));
    });
  }

  async parse(file: File, progress?: WorkerProgressHandler): Promise<ParsedWorkbookMeta> {
    const buffer = await file.arrayBuffer();
    const requestId = this.nextRequestId();
    return this.send<ParsedWorkbookMeta>(
      {
        type: 'parse',
        requestId,
        fileName: file.name,
        fileSize: file.size,
        buffer,
      },
      [buffer],
      progress,
    );
  }

  inspect(
    mappings: readonly SheetMapping[],
    settings: InspectionSettings,
    progress?: WorkerProgressHandler,
  ): Promise<InspectionPayload> {
    const requestId = this.nextRequestId();
    return this.send<InspectionPayload>(
      { type: 'inspect', requestId, mappings, settings },
      [],
      progress,
    );
  }

  terminate(): void {
    this.worker.terminate();
    this.rejectAll(new Error('检查已取消。'));
  }

  private send<T>(
    request: WorkerRequest,
    transfer: Transferable[],
    progress?: WorkerProgressHandler,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const pending: PendingRequest<T> =
        progress === undefined ? { resolve, reject } : { resolve, reject, progress };
      this.pending.set(request.requestId, pending as PendingRequest<unknown>);
      this.worker.postMessage(request, transfer);
    });
  }

  private handleResponse(response: WorkerResponse): void {
    const pending = this.pending.get(response.requestId);
    if (pending === undefined) return;
    if (response.type === 'progress') {
      pending.progress?.(response);
      return;
    }
    this.pending.delete(response.requestId);
    if (response.type === 'error') {
      pending.reject(new Error(response.message));
    } else if (response.type === 'parse-success') {
      pending.resolve(response.meta);
    } else {
      pending.resolve(response.payload);
    }
  }

  private nextRequestId(): string {
    this.requestSequence += 1;
    return `request-${String(this.requestSequence)}`;
  }

  private rejectAll(error: Error): void {
    for (const pending of this.pending.values()) pending.reject(error);
    this.pending.clear();
  }
}
